import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Flex,
    Grid,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { useContext, useEffect } from 'react';
import InventoryItemImage from '../../../components/inventory/InventoryItemImage';
import { useQueryClient } from '@tanstack/react-query';
import SessionContext from '../../../contexts/SessionContext';
import { isInventoryAccessDeniedError } from '../../../utils/inventoryAccess';
import Pagination from '../../../components/Pagination';
import { Tooltip } from '../../../ui';
import {
    getInventoryStatusLabel,
    getInventoryStatusBadgeColors,
} from '../../../utils/inventoryConstants';
import MfgPartNumberWithCopy from '../../../components/inventory/MfgPartNumberWithCopy';
import { getAlternateCount, getPrimaryPartNumber } from '../../../utils/inventoryPartNumbers';
import type { InventoryListPageModel } from './useInventoryListPage';

const CARD_WIDTH_PX = 240;

export function InventoryListGrid({ page }: { page: InventoryListPageModel }) {
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const {
        isLoading,
        isRefetching,
        error,
        pageItems,
        searchQuery,
        handleRowClick,
        currentPage,
        setCurrentPage,
        totalPages,
        hasNextPage,
    } = page;

    useEffect(() => {
        if (!error || !isInventoryAccessDeniedError(error)) return;
        queryClient.invalidateQueries({
            queryKey: ['/account/validate', sessionContext?.sessionId ?? null],
        });
    }, [error, queryClient, sessionContext?.sessionId]);

    if (isLoading || (isRefetching && pageItems.length === 0)) {
        return (
            <Flex justify="center" align="center" flex={1} py={16}>
                <Spinner color="brand.solid" size="lg" />
            </Flex>
        );
    }

    if (error && isInventoryAccessDeniedError(error)) {
        return (
            <Box px={6}>
                <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    Inventory is not enabled for this account.
                </Alert>
            </Box>
        );
    }

    if (error) {
        return (
            <Box px={6}>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    Failed to load inventory.
                </Alert>
            </Box>
        );
    }

    if (pageItems.length === 0) {
        return (
            <Flex justify="center" align="center" flex={1} py={16} px={6}>
                <Text color="gray.500" fontSize="sm">
                    {searchQuery
                        ? 'No items match your search.'
                        : 'No inventory items yet.'}
                </Text>
            </Flex>
        );
    }

    return (
        <Flex direction="column" flex={1} minH={0}>
            <Box flex={1} overflowY="auto" opacity={isRefetching ? 0.7 : 1} px={6}>
                {/* minmax + 1fr: columns flex to fill the row, so no dead gutter on the right. */}
                <Grid templateColumns={`repeat(auto-fill, minmax(${CARD_WIDTH_PX}px, 1fr))`} gap={4}>
                    {pageItems.map((item) => {
                        const statusLabel = getInventoryStatusLabel(item);
                        const badgeColors = getInventoryStatusBadgeColors(item);
                        return (
                            <Box
                                key={item._id}
                                as="button"
                                type="button"
                                display="flex"
                                flexDirection="column"
                                textAlign="left"
                                borderWidth="1px"
                                borderColor="border.subtle"
                                borderRadius="lg"
                                overflow="hidden"
                                bg="white"
                                _hover={{ borderColor: 'border.default', boxShadow: 'none' }}
                                transition="all 0.15s"
                                onClick={() => handleRowClick(item)}
                            >
                                <Box
                                    aspectRatio={4 / 3}
                                    flexShrink={0}
                                    bg="bg.surface"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    overflow="hidden"
                                >
                                    <InventoryItemImage src={item.imageUrl} alt={item.name} />
                                </Box>
                                <Tooltip
                                    label={item.name}
                                    isDisabled={!item.name}
                                    placement="top"
                                    hasArrow
                                    openDelay={200}
                                    maxW={`${CARD_WIDTH_PX}px`}
                                    sx={{
                                        maxW: `${CARD_WIDTH_PX}px`,
                                        whiteSpace: 'normal',
                                        textAlign: 'left',
                                    }}
                                >
                                <Box p={3}>
                                    <Flex justify="space-between" align="flex-start" gap={2} mb={1}>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="semibold"
                                            color="fg.default"
                                            noOfLines={2}
                                            flex={1}
                                        >
                                            {item.name}
                                        </Text>
                                        <Badge
                                            bg={badgeColors.bg}
                                            color={badgeColors.color}
                                            fontSize="xs"
                                            borderRadius="md"
                                            textTransform="none"
                                            flexShrink={0}
                                        >
                                            {statusLabel}
                                        </Badge>
                                    </Flex>
                                    <Box
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    >
                                        <MfgPartNumberWithCopy
                                            partNumber={getPrimaryPartNumber(item)}
                                            alternateCount={getAlternateCount(item)}
                                            fontSize="xs"
                                        />
                                    </Box>
                                </Box>
                                </Tooltip>
                            </Box>
                        );
                    })}
                </Grid>
            </Box>
            <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    hasNextPage={hasNextPage}
                    onPageChange={setCurrentPage}
                    isLoading={isRefetching}
                />
            </Box>
        </Flex>
    );
}
